// @ts-nocheck
import { IAddress, UserOrder } from "../../../interfaces";
import EditOrderAddress from "./EditOrderAddress";
import { useState } from "react";
import useOrder from "../../../hooks/useOrder";
import { useUser } from "../../../contexts";
import { API, graphqlOperation } from "aws-amplify";
import { createActivityLog } from "../../../graphql/mutations";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

export interface TAddress extends IAddress {
  id: string;
}

// Utility function to format date

const formatDate = (dateString: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);

  const options: Intl.DateTimeFormatOptions = {
    day: "numeric",
    month: "long",
    year: "numeric",
  };

  let languageCode: string;

  switch (i18n.language) {
    case "de":
      languageCode = "de-DE";
      break;
    case "en":
    default:
      languageCode = "en-GB";
      break;
  }

  return date.toLocaleDateString(languageCode, options);
};

const OrderAddressCard = ({
  data,
  onUpdateOrder = () => {},
  isShippingAddressEditable = false,
  isBillingAddressEditable = false,
}: {
  data: UserOrder;
  onUpdateOrder?: (value: UserOrder) => void;
  isShippingAddressEditable?: boolean;
  isBillingAddressEditable?: boolean;
}) => {
  const { handleOrderUpdate } = useOrder();
  const [{ user }] = useUser();
  const { t } = useTranslation();

  const UpdateConfirm = async (
    newAddress: IAddress,
    oldAddress: IAddress,
    orderId?: string,
    updatedBy?: string,
    addressType?: "Billing" | "Shipping"
  ) => {
    const addressChange = {
      oldAddress: oldAddress,
      newAddress: newAddress,
      addressType: addressType,
    };
    const logDetails = {
      operation: "CHANGE",
      field: "address",
      value: JSON.stringify(addressChange),
      updatedBy: updatedBy,
      orderItemActivityLogsId: orderId,
      typename: "activityLog",
    };
    try {
      await API.graphql(
        graphqlOperation(createActivityLog, { input: logDetails })
      );
    } catch (error) {
      console.error("Error creating activity log:", error);
    }
  };

  const onChangeBillingAddress = async (
    formData: IAddress,
    updatedEmail?: string
  ) => {
    delete formData.email;
    const success = await handleOrderUpdate(
      {
        id: data.id,
        billingAdress: formData,
        email: updatedEmail ?? data.email,
      },
      `Billing address updated for order ${data.id}.`
    );

    if (success) {
      try {
        if (user?.isAdmin || user?.isManufacturer) {
          await UpdateConfirm(
            formData,
            data.billingAdress,
            data.id,
            data.lastUpdatedBy,
            "Billing"
          );
        } else {
          await UpdateConfirm(
            formData,
            data.billingAdress,
            data.id,
            user?.email || data.email || "",
            "Billing"
          );
        }
        onUpdateOrder(success);
      } catch (error) {
        console.error("Error updating billing address:", error);
      }
    }
  };

  const onChangeShippingAddress = async (formData: IAddress) => {
    const success = await handleOrderUpdate(
      { id: data.id, shippingAdress: formData },
      `Shipping address updated for order ${data.id}.`
    );

    if (success) {
      try {
        if (user?.isAdmin || user?.isManufacturer) {
          await UpdateConfirm(
            formData,
            data.shippingAdress,
            data.id,
            data.lastUpdatedBy,
            "Shipping"
          );
        } else {
          await UpdateConfirm(
            formData,
            data.shippingAdress,
            data.id,
            user?.email || data.email || "",
            "Shipping"
          );
        }
        onUpdateOrder(success);
      } catch (error) {
        console.error("Error updating shipping address:", error);
      }
    }
  };

return (
  <div className="h-full w-full flex flex-col">
    <div className="card-auto pb-8 px-8 pt-2 address-details flex flex-col gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <EditOrderAddress
          orderEmail={data.email ?? ""}
          isEditable={isBillingAddressEditable}
          label={"billingAddress"}
          address={data.billingAdress}
          onAddressChange={onChangeBillingAddress}
          onEmailChange={(email) => onUpdateOrder({ ...data, email })}
        />
        <EditOrderAddress
          isEditable={isShippingAddressEditable}
          label={"shippingAddress"}
          address={data.shippingAdress}
          onAddressChange={onChangeShippingAddress}
        />
      </div>
      
      <div className="address-details__address box-sizing-bb">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <div>
            <p className="text-sm lg:text-md font-bold">{t("orderDate")}</p>
            <p className="text-sm wrap-break-word">
              {formatDate(data.uploadedDate?.toString() || "")}
            </p>
          </div>
          <div>
            <p className="text-sm lg:text-md font-bold">
              {t("deliveryDate")}
            </p>
            <p className="text-sm wrap-break-word">
              {formatDate(data.deliveryDate?.toString() || "")}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
);
};

export default OrderAddressCard;
